import { useState, useEffect, useCallback, useRef } from 'react';

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
];

// ─── Cache ────────────────────────────────────────────────────────────────────
const CACHE = {};

async function translateChunk(text, targetLang, retries = 3) {
  if (!text || !text.trim()) return text;
  const key = `${targetLang}::${text}`;
  if (CACHE[key]) return CACHE[key];

  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const translated = data[0]
        .filter(Boolean)
        .map(chunk => chunk[0] ?? '')
        .join('');
      CACHE[key] = translated;
      return translated;
    } catch (err) {
      if (attempt === retries - 1) throw err;
      await new Promise(r => setTimeout(r, 300 * 2 ** attempt));
    }
  }
}

// ─── Translate plain text (split into ≤ 4500-char sentence chunks) ────────────
function splitText(text, maxLen = 4500) {
  const chunks = [];
  const sentences = text.match(/[^.!?\n]+[.!?\n]*/g) ?? [text];
  let current = '';
  for (const s of sentences) {
    if ((current + s).length > maxLen && current.length > 0) {
      chunks.push(current.trim());
      current = s;
    } else {
      current += s;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

async function translateText(text, targetLang) {
  if (!text || targetLang === 'en') return text;
  const chunks = splitText(text);
  const results = await Promise.all(chunks.map(c => translateChunk(c, targetLang)));
  return results.join(' ');
}

// ─── Translate HTML by walking text nodes, batched with SEP sentinel ──────────
const SEP = '\n|||SEP|||\n';

async function translateHTML(html, targetLang) {
  if (!html || targetLang === 'en') return html;

  const div = document.createElement('div');
  div.innerHTML = html;

  // Collect all non-empty text nodes
  const textNodes = [];
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null, false);
  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.trim()) textNodes.push(node);
  }

  // Batch into groups ≤ 4500 chars
  const batches = [];
  let curNodes = [], curText = '';
  for (const tn of textNodes) {
    const add = (curText ? SEP : '') + tn.textContent;
    if ((curText + add).length > 4500 && curNodes.length > 0) {
      batches.push({ nodes: curNodes, text: curText });
      curNodes = [tn];
      curText = tn.textContent;
    } else {
      curNodes.push(tn);
      curText += (curText ? SEP : '') + tn.textContent;
    }
  }
  if (curNodes.length > 0) batches.push({ nodes: curNodes, text: curText });

  // Translate all batches in parallel and write back
  await Promise.all(batches.map(async (batch) => {
    try {
      const translated = await translateChunk(batch.text, targetLang);
      const parts = translated.split(/\n?\|\|\|SEP\|\|\|\n?/);
      batch.nodes.forEach((tn, i) => {
        const leading  = tn.textContent.match(/^\s*/)[0];
        const trailing = tn.textContent.match(/\s*$/)[0];
        tn.textContent = leading + (parts[i] ?? tn.textContent).trim() + trailing;
      });
    } catch {
      // leave originals on batch failure
    }
  }));

  return div.innerHTML;
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * Props:
 *   article    – { _id, title, content, excerpt }   (original English values)
 *   onTranslated – ({ title, content, excerpt }) => void
 *
 * The parent is responsible for rendering title / content / excerpt from
 * whatever onTranslated gives back.  This component only translates those
 * three fields and nothing else on the page.
 */
const LanguageSwitcher = ({ article, onTranslated }) => {
  const [activeLang, setActiveLang] = useState('en');
  const [loading, setLoading]       = useState(false);
  const [open, setOpen]             = useState(false);
  const [error, setError]           = useState(null);
  const abortRef                    = useRef(false);

  // Reset to English whenever the article changes
  useEffect(() => {
    abortRef.current = true;
    setActiveLang('en');
    setError(null);
    onTranslated({
      title:   article.title,
      content: article.content,
      excerpt: article.excerpt,
    });
    setTimeout(() => { abortRef.current = false; }, 0);
  }, [article._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLanguageChange = useCallback(async (lang) => {
    setOpen(false);
    if (lang === activeLang) return;

    abortRef.current = false;
    setActiveLang(lang);
    setError(null);

    // Restore English immediately
    if (lang === 'en') {
      onTranslated({
        title:   article.title,
        content: article.content,
        excerpt: article.excerpt,
      });
      return;
    }

    setLoading(true);
    try {
      // Translate title, excerpt (plain text) and content (HTML) in parallel
      const [title, excerpt, content] = await Promise.all([
        translateText(article.title,   lang),
        translateText(article.excerpt, lang),
        translateHTML(article.content, lang),
      ]);

      if (abortRef.current) return; // article changed mid-flight — discard

      onTranslated({ title, content, excerpt });
    } catch (err) {
      console.error('Translation error:', err);
      setError('Translation failed. Showing original.');
      onTranslated({
        title:   article.title,
        content: article.content,
        excerpt: article.excerpt,
      });
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  }, [activeLang, article, onTranslated]);

  const currentLang = LANGUAGES.find(l => l.code === activeLang);

  return (
    <div className="lang-switcher-wrap">
      <div className="lang-switcher-label">🌐 Read in:</div>

      <div className="lang-switcher-dropdown">
        <button
          className="lang-switcher-btn"
          onClick={() => setOpen(o => !o)}
          disabled={loading}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {loading ? (
            <span className="lang-spinner" aria-label="Translating" />
          ) : (
            <span className="lang-flag">{currentLang?.native}</span>
          )}
          <span className="lang-chevron">{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div className="lang-dropdown-menu" role="listbox">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                role="option"
                aria-selected={activeLang === lang.code}
                className={`lang-option ${activeLang === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="lang-native">{lang.native}</span>
                <span className="lang-english">{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <span className="lang-translating" aria-live="polite">
          Translating article…
        </span>
      )}

      {error && (
        <span className="lang-error" role="alert">{error}</span>
      )}
    </div>
  );
};

export default LanguageSwitcher;